// Currency Service for EmailValidator
class CurrencyService {
  constructor() {
    // Centralized pricing configuration
    this.pricingConfig = {
      'USD': {
        'Pro': {
          'monthly': 1400, // $14.00 in cents
          'yearly': 11620  // $116.20 in cents (17% discount)
        },
        'Enterprise': {
          'monthly': 4900, // $49.00 in cents
          'yearly': 40670  // $406.70 in cents (17% discount)
        }
      },
      'INR': {
        'Pro': {
          'monthly': 142100, // ₹1,421.00 in paise (including 18% GST)
          'yearly': 1177200  // ₹11,772.00 in paise (including 18% GST, 17% discount)
        },
        'Enterprise': {
          'monthly': 497350, // ₹4,973.50 in paise (including 18% GST)
          'yearly': 4124005  // ₹41,240.05 in paise (including 18% GST, 17% discount)
        }
      }
    };

    // Currency display info
    this.currencyInfo = {
      'USD': { symbol: '$', name: 'US Dollar' },
      'INR': { symbol: '₹', name: 'Indian Rupee' }
    };
    
    this.userCurrency = null;
    this.userCountry = null;
    this.lastUpdateTime = null;
  }

  // Save pricing state to localStorage
  savePricingState(currency, billingCycle, plan = null) {
    const state = {
      currency: currency,
      billingCycle: billingCycle,
      plan: plan,
      timestamp: Date.now()
    };
    localStorage.setItem('pricingState', JSON.stringify(state));
    console.log('Pricing state saved:', state);
  }

  // Load pricing state from localStorage
  loadPricingState() {
    const state = localStorage.getItem('pricingState');
    if (state) {
      try {
        const parsedState = JSON.parse(state);
        console.log('Pricing state loaded:', parsedState);
        return parsedState;
      } catch (error) {
        console.warn('Failed to parse pricing state:', error);
        return null;
      }
    }
    return null;
  }

  // Clear pricing state from localStorage
  clearPricingState() {
    localStorage.removeItem('pricingState');
    console.log('Pricing state cleared');
  }

  // Get pricing for a specific plan and currency
  getPricing(planName, billingCycle, currency = null) {
    const targetCurrency = currency || this.userCurrency || 'USD';
    
    if (!this.pricingConfig[targetCurrency] || !this.pricingConfig[targetCurrency][planName]) {
      throw new Error(`Pricing not configured for ${planName} in ${targetCurrency}`);
    }

    const amount = this.pricingConfig[targetCurrency][planName][billingCycle];
    if (amount === undefined) {
      throw new Error(`Pricing not configured for ${planName} ${billingCycle} in ${targetCurrency}`);
    }

    const currencyInfo = this.currencyInfo[targetCurrency];
    
    const result = {
      amount: amount,
      currency: targetCurrency,
      symbol: currencyInfo.symbol,
      formatted: this.formatPrice(amount, targetCurrency, currencyInfo.symbol),
      originalAmountUSD: this.getUSDEquivalent(planName, billingCycle),
      originalCurrency: 'USD',
      gstApplicable: targetCurrency === 'INR' && planName !== 'Starter'
    };

    console.log(`Pricing for ${planName} ${billingCycle} ${targetCurrency}:`, result);
    return result;
  }

  // Get USD equivalent for reference
  getUSDEquivalent(planName, billingCycle) {
    const basePrices = {
      'Pro': { monthly: 14, yearly: 140 },
      'Enterprise': { monthly: 49, yearly: 490 }
    };
    
    let amount = basePrices[planName]?.[billingCycle];
    if (billingCycle === 'yearly') {
      amount = Math.round(amount * 0.83); // 17% discount
    }
    return amount;
  }

  // Format price with currency symbol
  formatPrice(amount, currency, symbol) {
    if (amount === 0) return 'Free';
    
    // Format based on currency
    switch (currency) {
      case 'USD':
        return `${symbol}${(amount / 100).toFixed(2)}`;
      case 'INR':
        return `${symbol}${(amount / 100).toLocaleString('en-IN')}`;
      default:
        return `${symbol}${(amount / 100).toLocaleString('en-IN')}`;
    }
  }

  // Get all supported currencies
  getSupportedCurrencies() {
    return Object.keys(this.currencyInfo).map(code => ({
      code,
      ...this.currencyInfo[code]
    }));
  }

  // Get current user currency
  getCurrentCurrency() {
    return this.userCurrency || 'USD';
  }

  // Get current user country
  getCurrentCountry() {
    return this.userCountry || 'US';
  }

  // Get currency display info
  getCurrencyInfo(currency) {
    console.log('Getting currency info for:', currency);
    console.log('Available currencies:', Object.keys(this.currencyInfo));
    const result = this.currencyInfo[currency] || null;
    console.log('Currency info result:', result);
    return result;
  }

  // Detect user's location and currency
  async detectUserLocation() {
    try {
      // Try to get location from IP
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      this.userCountry = data.country_code;
      
      // Set currency based on country: India = INR, others = USD
      if (data.country_code === 'IN') {
        this.userCurrency = 'INR';
      } else {
        this.userCurrency = 'USD';
      }
      
      return {
        country: this.userCountry,
        currency: this.userCurrency,
        currencyInfo: this.currencyInfo[this.userCurrency]
      };
    } catch (error) {
      console.warn('Could not detect location, defaulting to USD:', error);
      this.userCurrency = 'USD';
      this.userCountry = 'US';
      return {
        country: 'US',
        currency: 'USD',
        currencyInfo: this.currencyInfo['USD']
      };
    }
  }

  // Initialize (simplified - no exchange rate fetching needed)
  async initializeRates() {
    console.log('Currency service initialized with fixed pricing');
  }

  // Get pricing with GST (simplified - GST already included in pricing)
  getPricingWithGST(planName, billingCycle, currency = null) {
    const pricing = this.getPricing(planName, billingCycle, currency);
    
    if (pricing.gstApplicable) {
      // For INR, GST is already included in the pricing
      const baseAmount = pricing.amount * 0.847; // Remove 18% GST to get base amount
      const gstAmount = pricing.amount - baseAmount;
      
      const gst = {
        amount: gstAmount,
        rate: 0.18,
        formatted: `₹${(gstAmount / 100).toLocaleString('en-IN')}`,
        totalAmount: pricing.amount,
        totalFormatted: pricing.formatted
      };

      return {
        ...pricing,
        gst,
        baseAmount: baseAmount,
        baseFormatted: this.formatPrice(baseAmount, pricing.currency, pricing.symbol),
        finalAmount: pricing.amount,
        finalFormatted: pricing.formatted
      };
    }
    
    // For USD plans, add finalFormatted for consistency
    return {
      ...pricing,
      finalFormatted: pricing.formatted
    };
  }

  // Test pricing calculation (for debugging)
  testPricing() {
    console.log('Testing centralized pricing:');
    
    const usdProMonthly = this.getPricing('Pro', 'monthly', 'USD');
    console.log('USD Pro Monthly:', usdProMonthly);
    
    const usdProYearly = this.getPricing('Pro', 'yearly', 'USD');
    console.log('USD Pro Yearly:', usdProYearly);
    
    const inrProMonthly = this.getPricing('Pro', 'monthly', 'INR');
    console.log('INR Pro Monthly:', inrProMonthly);
    
    const inrProYearly = this.getPricing('Pro', 'yearly', 'INR');
    console.log('INR Pro Yearly:', inrProYearly);
    
    return { usdProMonthly, usdProYearly, inrProMonthly, inrProYearly };
  }
}

export default new CurrencyService(); 